import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  FileText,
  DollarSign,
  Warehouse,
  BarChart3,
  LogOut,
  Menu,
  X,
  Tag,
  Receipt,
  FileBarChart,
  Upload,
  Wallet,
  Lock,
  TrendingUp,
  Download,
  CreditCard,
  ClipboardList,
  Percent,
  Globe,
  BookOpen,
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu items with permission requirements
  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'view_dashboard' },
    { path: '/dashboard/customers', icon: Users, label: 'Customers', permission: 'view_customers' },
    { path: '/dashboard/products', icon: Package, label: 'Products', permission: 'view_products' },
    { path: '/dashboard/price-lists', icon: Tag, label: 'Price Lists', permission: 'view_price_lists' },
    { path: '/dashboard/purchases', icon: ShoppingCart, label: 'Purchases', permission: 'view_purchases' },
    { path: '/dashboard/orders', icon: FileText, label: 'Orders', permission: 'view_orders' },
    { path: '/dashboard/inventory', icon: Warehouse, label: 'Inventory', permission: 'view_products' },
    { path: '/dashboard/expenses', icon: Receipt, label: 'Expenses', permission: 'view_expenses' },
    { path: '/dashboard/reports', icon: BarChart3, label: 'Reports', permission: 'view_reports' },
    { path: '/dashboard/analytics', icon: TrendingUp, label: 'Analytics', permission: 'view_analytics' },
    { path: '/dashboard/reports-download', icon: Download, label: 'Download Data', permission: 'export_reports' },
    { path: '/dashboard/bulk-upload', icon: Upload, label: 'Bulk Upload', permission: 'bulk_upload_products' },
    { path: '/dashboard/financial-management', icon: Wallet, label: 'Finance', permission: 'view_finance' },
    { path: '/dashboard/profit-tracking', icon: TrendingUp, label: 'Profit Tracking', permission: 'view_profit_tracking' },
    { path: '/dashboard/vat-tracking', icon: Percent, label: 'VAT Tracking', permission: 'view_reports' },
    { path: '/dashboard/audit-log', icon: ClipboardList, label: 'Activity Log', permission: 'view_audit_log' },
    { path: '/dashboard/landing', icon: Globe, label: 'Landing Page', permission: 'manage_landing' },
    { path: '/dashboard/users', icon: Users, label: 'Users', permission: 'view_users' },
    { path: '/dashboard/blog', icon: BookOpen, label: 'Blog', permission: 'view_users' },
  ];

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems.filter(item => {
    return hasPermission(item.permission);
  });

  // Check if current route is accessible
  const currentMenuItem = menuItems.find(item => item.path === location.pathname);
  const hasAccessToCurrentRoute = currentMenuItem 
    ? hasPermission(currentMenuItem.permission)
    : true;

  // If user doesn't have access to current route, redirect to dashboard
  if (!hasAccessToCurrentRoute && location.pathname !== '/dashboard') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <img 
                src="/bison-logo.png" 
                alt="Bison Food Solutions" 
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <h1 className="text-base font-heading font-bold text-white">Bison Food Solutions Ltd</h1>
                <p className="text-xs text-white/60">Management System</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:bg-white/10 p-2 rounded"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
          
          <div className="p-4 border-t border-white/10">
            <div className="mb-4 text-white/80 text-sm">
              <div className="font-medium text-white">{user?.name}</div>
              <div className="text-xs capitalize">{user?.role}</div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-border sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-foreground hover:bg-accent p-2 rounded"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h2>
            <div />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;